import { useState, useRef, useEffect } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, MoreVertical, Edit, Trash2 } from "lucide-react";
import {
  SortableContext,
  verticalListSortingStrategy,
  rectSortingStrategy
} from "@dnd-kit/sortable";
import WarningDialog from "./WarningDialog";

export function CategoryItem({
  category,
  isSelected,
  onSelect,
  onEdit,
  onDelete,
  onStatusChange,
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const dropdownRef = useRef(null);
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: category?.categoryId?.toString() });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex justifty-between items-center rounded-md border border-input px-4 py-3 text-sm transition-colors  ${
        isSelected
          ? "border-1 border-green-600 bg-accent"
          : "hover:bg-accent/50"
      } ${category.status !== "active" ? "bg-red-100" : " bg-white"}`}
      onClick={() => {
        onSelect(category);
      }}
    >
      <WarningDialog
        title={"please confirm to delete ?"}
        isOpen={showDeleteWarning}
        onClose={() => setShowDeleteWarning(false)}
        onSubmit={() => onDelete(category.categoryId)}
      />

      <div className="flex">
        <div
          {...attributes}
          {...listeners}
          className="mr-2 cursor-grab active:cursor-grabbing"
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </div>
        <span className="font-medium">{category.categoryName}</span>
      </div>

      <div ref={dropdownRef} className="ml-auto">
        <div className="relative">
          {isSelected && (
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex h-6 w-6 items-center justify-center rounded-md hover:bg-gray-100"
            >
              <MoreVertical className="h-4 w-4" />
            </button>
          )}

          {dropdownOpen && (
            <div className="absolute right-0 top-full z-10 mt-1 w-36 rounded-md border border-gray-200 bg-white shadow-md">
              <div className="py-1">
                <button
                  onClick={() => {
                    onEdit(category.categoryId);
                    setDropdownOpen(false);
                  }}
                  className="flex w-full items-center px-4 py-2 text-xs hover:bg-gray-100"
                >
                  <Edit className="mr-2 h-3 w-3" />
                  Edit
                </button>
                <button
                  onClick={() => {
                    onStatusChange(
                      category,
                      category.status === "active" ? "inactive" : "active"
                    );
                  }}
                  className="flex w-full items-center px-4 py-2 text-xs hover:bg-gray-100"
                >
                  {category.status === "active" ? "Inactivate" : "Activate"}
                </button>
                <button
                  onClick={() => {
                    setShowDeleteWarning(true);
                    setDropdownOpen(false);
                  }}
                  className="flex w-full items-center px-4 py-2 text-xs text-red-600 hover:bg-gray-100"
                >
                  <Trash2 className="mr-2 h-3 w-3" />
                  Delete
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function CategoryList({
  categories,
  selectedCategory,
  onSelectCategory,
  onEdit,
  onDelete,
  onStatusChange,
}) {
  return (
    <SortableContext
      items={categories.map((cat) => cat?.categoryId?.toString() )}
      strategy={verticalListSortingStrategy}
    >
      <div className="flex flex-col space-y-2 max-h-[calc(100vh-184px)] overflow-hidden overflow-y-scroll hide-scrollbar">
        {categories.map((category) => (
          <CategoryItem
            key={category.categoryId}
            category={category}
            isSelected={category.categoryId === selectedCategory?.categoryId}
            onSelect={onSelectCategory}
            onEdit={onEdit}
            onDelete={onDelete}
            onStatusChange={onStatusChange}
          />
        ))}
      </div>
    </SortableContext>
  );
}
